import { color, XStack, YStack } from '@mythical/ui'
import { useEffect, useRef, useState } from 'react'
import { Input } from 'app/components/primitive/Form'
import { IconClose, IconSearch } from 'app/components/primitive/Icon'
import { useSearchContext } from './SearchContext'
import { IconButton } from 'app/components/primitive/IconButton'

const DEBOUNCE_MS = 350

interface SearchBarProps {
  onSubmit: () => void
  id: string
}

const SearchBar = ({ onSubmit, id }: SearchBarProps) => {
  const searchValue = useSearchContext((state) => state.filters.search)
  const setFilter = useSearchContext((state) => state.setFilter)
  const [inputValue, setInputValue] = useState(searchValue)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Debounced sync to Zustand store
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(() => {
      setFilter('search', inputValue)
    }, DEBOUNCE_MS)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [inputValue, setFilter])

  const handleClear = () => {
    setInputValue('')
    setFilter('search', '')
  }

  const handleSubmit = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    setFilter('search', inputValue)
    onSubmit()
  }

  return (
    <XStack pos="relative" ai="center" fg={1} fs={1}>
      <Input
        id={id}
        theme="form"
        placeholderTextColor={color.fgPrimary_op50}
        placeholder="Search"
        onChangeText={setInputValue}
        onSubmitEditing={handleSubmit}
        value={inputValue}
        br="$pill"
        pr="$7"
        pl="$8"
        h={40}
        fullWidth
        blurOnSubmit
        returnKeyType="search"
      />

      <YStack pos="absolute" left="$3" top="$0" bottom="$0" jc="center">
        <IconSearch size={28} />
      </YStack>

      {inputValue.length > 0 && (
        <YStack
          jc="center"
          pos="absolute"
          right="$3"
          height="100%"
          zIndex="$1"
          top="$0"
          bottom="$0"
        >
          <IconButton
            size="$2"
            onPress={handleClear}
          >
            <IconClose size={20} />
          </IconButton>
        </YStack>
      )}
    </XStack>
  )
}

export default SearchBar
